import {Component, input, output, inject} from '@angular/core';
import {DefaultWorkingTimesFormComponent} from './default-working-times-form/default-working-times-form.component';
import {
  DefaultWorkingTimesDeleteComponent
} from './default-working-times-delete/default-working-times-delete.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DataService} from '../../../services/store/data.service';
import {StoreDefaultWorkingTime} from '../../../classes/models/store/StoreDefaultWorkingTime';
import {WorkingHoursService} from '../../../services/store/working-hours.service';
import {DayOfWeek, dayOfWeekValues} from '../../../classes/enums/DayOfWeek';
import {SHARED_IMPORTS} from "../../../shared/standalone-imports";

/**
 * This component handles displaying and editing store working times.
 */
@Component({
  selector: 'app-default-working-times',
  templateUrl: './default-working-times.component.html',
  styleUrls: ['./default-working-times.component.less'],
  imports: [SHARED_IMPORTS]
})
export class DefaultWorkingTimesComponent {
  private modalService = inject(NgbModal);
  private workingHoursService = inject(WorkingHoursService);
  dataService = inject(DataService);

  /** An identifier of a store. */
  readonly storeId = input<string>(undefined);
  /** A list of default working hours for each day of week. */
  readonly defaultWorkingTimes = input<StoreDefaultWorkingTime[]>();
  /** An event to refresh store page data. */
  getStoreDetailsEvent = output();

  /** Assigns days of week values to be used in HTML. */
  daysOfWeekValues: DayOfWeek[] = dayOfWeekValues();

  /**
   * Checks if data can be edited at the given date.
   * @param date the date on which data might be edited.
   */
  canEditAtDate(date: string): boolean {
    return this.dataService.canEditAtDate(date);
  }

  /**
   * Opens a modal window to create new working time.
   */
  openCreate(): void {
    const modalRef = DefaultWorkingTimesFormComponent.open(this.modalService, {
      storeId: this.storeId(),
      previousDefaultWorkingTime: this.workingHoursService.getLatestDefaultWorkingTime(this.defaultWorkingTimes()),
      weekExists: false
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }

  /**
   * Opens a modal window to update existing working time.
   */
  openUpdate(defaultWorkingTime: StoreDefaultWorkingTime): void {
    const modalRef = DefaultWorkingTimesFormComponent.open(this.modalService, {
      storeId: this.storeId(),
      previousDefaultWorkingTime: defaultWorkingTime,
      weekExists: true
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }

  /**
   * Opens a modal to delete an existing working time.
   * @param startDate the Monday of the week, from which default working times should be deleted.
   */
  openDelete(startDate: string): void {
    const modalRef = DefaultWorkingTimesDeleteComponent.open(this.modalService, {
      storeId: this.storeId(),
      startDate: startDate
    });
    modalRef.result.then(() => {
      this.getStoreDetailsEvent.emit();
    });
  }
}